/**
 * Project API client — fetches project metadata + mints signed tileset URLs.
 *
 * All calls go through `authenticatedFetch` which adds the Bearer header
 * and auto-refreshes JWT on 401.  Endpoint paths below are the planned
 * shape; once the real backend is plugged in, only the constants here
 * (and the response shapes) need to change.
 *
 * Endpoint contract (subject to backend confirmation):
 *   GET  /api/projects/                         → { items: ProjectSummary[] }
 *   GET  /api/projects/<id>                     → ProjectMetadata
 *   POST /api/projects/<id>/tileset-url         → { signedUrl, expiresAt }
 */

import { authenticatedFetch } from '../auth/webAuthClient'

export interface ProjectSummary {
  id: string
  name: string
  hasTileset: boolean
  updatedAt: string
  thumbnailUrl?: string
  /** ISO timestamp when the published tileset will be removed from Spaces. */
  expiresAt?: string
}

export interface ProjectMetadata extends ProjectSummary {
  utmEpsg?: number | null
  bbox?: [number, number, number, number, number, number]   // [minE, minN, minAlt, maxE, maxN, maxAlt]
}

export interface SignedTilesetUrl {
  signedUrl: string
  expiresAt: string                              // ISO timestamp
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const resp = await authenticatedFetch('/api/projects/')
  if (!resp.ok) {
    throw new Error(`Failed to list projects: HTTP ${resp.status}`)
  }
  const data = (await resp.json()) as { items?: ProjectSummary[] }
  return data.items ?? []
}

export async function getProject(id: string): Promise<ProjectMetadata> {
  const resp = await authenticatedFetch(`/api/projects/${encodeURIComponent(id)}/`)
  if (!resp.ok) {
    if (resp.status === 404) throw new Error(`Project not found: ${id}`)
    if (resp.status === 403) throw new Error(`Access denied for project ${id}`)
    throw new Error(`Failed to load project ${id}: HTTP ${resp.status}`)
  }
  return (await resp.json()) as ProjectMetadata
}

export async function mintTilesetUrl(id: string): Promise<SignedTilesetUrl> {
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(id)}/tileset-url/`,
    { method: 'POST' },
  )
  if (!resp.ok) {
    throw new Error(`Failed to mint tileset URL: HTTP ${resp.status}`)
  }
  return (await resp.json()) as SignedTilesetUrl
}

/**
 * Upload a JPEG thumbnail captured from the viewer canvas. Backend stores
 * it in Spaces under `projects/<id>/thumbnail.jpg` and updates the project
 * record. Failures are swallowed by the caller — thumbnail is best-effort.
 */
export async function uploadThumbnail(id: string, blob: Blob): Promise<void> {
  const fd = new FormData()
  fd.append('image', blob, 'thumbnail.jpg')
  const resp = await authenticatedFetch(
    `/api/projects/${encodeURIComponent(id)}/thumbnail/`,
    { method: 'POST', body: fd },
  )
  if (!resp.ok) {
    throw new Error(`Failed to upload thumbnail: HTTP ${resp.status}`)
  }
}
